import React from 'react';

const formatPrice = (p) => `R${Number(p || 0).toFixed(2)}`;

const formatDateLong = (dateStr) => {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-ZA', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  } catch {
    return dateStr;
  }
};

/**
 * Renders a beautiful fixed-size card for sharing as PNG.
 * Designed at 720px wide.
 */
const BookingShareCard = ({ booking, calculateTotal }) => {
  const total = calculateTotal ? calculateTotal(booking) : Number(booking.totalPrice || 0);
  const basePrice =
    Number(booking.optionPrice) ||
    Number(booking.price) ||
    Number(booking.servicePrice) ||
    0;
  const hasAddons = Array.isArray(booking.addons) && booking.addons.length > 0;
  const hasTransport = Number(booking.transportFee || 0) > 0;
  const coverImage =
    booking.professionalImage ||
    (Array.isArray(booking.media) && booking.media[0]?.url) ||
    null;

  return (
    <div
      style={{
        width: '720px',
        background: 'linear-gradient(180deg, #FFF5F6 0%, #FFFFFF 40%)',
        fontFamily: 'system-ui, -apple-system, sans-serif',
        padding: '0',
      }}
    >
      {/* Cover */}
      <div
        style={{
          width: '100%',
          height: '240px',
          background: '#FFF5F6',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {coverImage ? (
          <img
            src={coverImage}
            alt=""
            crossOrigin="anonymous"
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          <div
            style={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '80px',
            }}
          >
            ✨
          </div>
        )}
        {/* Skytone brand pill */}
        <div
          style={{
            position: 'absolute',
            top: '20px',
            left: '20px',
            background: 'rgba(255,255,255,0.95)',
            backdropFilter: 'blur(8px)',
            padding: '8px 16px',
            borderRadius: '999px',
            fontSize: '13px',
            fontWeight: 800,
            color: '#1e1917',
            letterSpacing: '0.05em',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
          }}
        >
          <span style={{ fontSize: '14px' }}>✦</span> SKYTONE
        </div>
      </div>

      {/* Body */}
      <div style={{ padding: '36px 40px 40px' }}>
        {/* Eyebrow */}
        <div
          style={{
            fontSize: '11px',
            fontWeight: 800,
            color: '#EFA9B6',
            letterSpacing: '0.2em',
            textTransform: 'uppercase',
            marginBottom: '12px',
          }}
        >
          Booking Confirmation
        </div>

        {/* Service title */}
        <h1
          style={{
            fontFamily: 'Georgia, "Times New Roman", serif',
            fontSize: '38px',
            lineHeight: 1.15,
            fontWeight: 600,
            color: '#1e1917',
            margin: 0,
            marginBottom: '8px',
          }}
        >
          {booking.serviceTitle || booking.serviceName || 'Service'}
        </h1>

        {/* Pro */}
        <p
          style={{
            fontSize: '16px',
            color: '#7a706b',
            margin: 0,
            marginBottom: '28px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          <span
            style={{
              display: 'inline-flex',
              width: '20px',
              height: '20px',
              borderRadius: '999px',
              background: '#FFF5F6',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '10px',
            }}
          >
            👤
          </span>
          with {booking.professionalName || 'Professional'}
        </p>

        {/* Date / time */}
        <div
          style={{
            background: '#FCF8F9',
            borderRadius: '20px',
            padding: '20px 24px',
            display: 'flex',
            gap: '24px',
            marginBottom: '20px',
          }}
        >
          <div style={{ flex: 1 }}>
            <div
              style={{
                fontSize: '10px',
                fontWeight: 800,
                color: '#a89e99',
                letterSpacing: '0.15em',
                textTransform: 'uppercase',
                marginBottom: '6px',
              }}
            >
              Date
            </div>
            <div style={{ fontSize: '16px', fontWeight: 600, color: '#1e1917' }}>
              {formatDateLong(booking.date)}
            </div>
          </div>
          <div style={{ width: '1px', background: '#eae4e0' }} />
          <div style={{ flex: 1 }}>
            <div
              style={{
                fontSize: '10px',
                fontWeight: 800,
                color: '#a89e99',
                letterSpacing: '0.15em',
                textTransform: 'uppercase',
                marginBottom: '6px',
              }}
            >
              Time
            </div>
            <div style={{ fontSize: '16px', fontWeight: 600, color: '#1e1917' }}>
              {booking.time || '—'}
            </div>
          </div>
        </div>

        {/* Breakdown */}
        <div
          style={{
            background: '#FCF8F9',
            borderRadius: '20px',
            padding: '20px 24px',
            marginBottom: '20px',
          }}
        >
          <div
            style={{
              fontSize: '10px',
              fontWeight: 800,
              color: '#a89e99',
              letterSpacing: '0.15em',
              textTransform: 'uppercase',
              marginBottom: '14px',
            }}
          >
            Breakdown
          </div>

          <Row
            label={booking.optionName || 'Service price'}
            value={formatPrice(basePrice)}
          />

          {hasAddons &&
            booking.addons.map((addon, idx) => (
              <Row
                key={addon.id || idx}
                label={`+ ${addon.name}`}
                value={`+${formatPrice(addon.price)}`}
                accent="#AF8BD4"
              />
            ))}

          {hasTransport && (
            <Row
              label="+ Transport fee"
              value={`+${formatPrice(booking.transportFee)}`}
              accent="#AF8BD4"
            />
          )}

          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              paddingTop: '14px',
              marginTop: '6px',
              borderTop: '1px solid #eae4e0',
              fontSize: '20px',
              fontWeight: 800,
              color: '#1e1917',
            }}
          >
            <span>Total</span>
            <span>{formatPrice(total)}</span>
          </div>
        </div>

        {/* Footer */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            fontSize: '13px',
            color: '#a89e99',
            marginTop: '8px',
          }}
        >
          <span>Booked on Skytone</span>
          <span style={{ fontWeight: 700, color: '#EFA9B6' }}>skytone.app</span>
        </div>
      </div>
    </div>
  );
};

const Row = ({ label, value, accent }) => (
  <div
    style={{
      display: 'flex',
      justifyContent: 'space-between',
      fontSize: '15px',
      marginBottom: '10px',
      color: '#4a4340',
    }}
  >
    <span>{label}</span>
    <span style={{ fontWeight: 700, color: accent || '#1e1917' }}>{value}</span>
  </div>
);

export default BookingShareCard;